import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deleteTenantMutation, type TenantRead } from "@harmonyso/tenants-sdk";
import { cellRequestOptions } from "@/lib/cells/cells";
import { invalidateCellTenantQueries } from "@/lib/cells/cell-queries";
import { Trash2, AlertTriangle, Shield, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

// Helper function to extract error message from unknown error
const getErrorMessage = (error: unknown): string => {
  if (error && typeof error === "object" && "response" in error) {
    const response = (error as { response?: { data?: { detail?: string } } })
      .response;
    if (response?.data?.detail) {
      return response.data.detail;
    }
  }
  if (error && typeof error === "object" && "message" in error) {
    return (error as { message: string }).message;
  }
  return "Unknown error occurred";
};

interface DeleteTenantDialogProps {
  tenant: TenantRead;
  trigger?: React.ReactNode;
}

export function DeleteTenantDialog({
  tenant,
  trigger,
}: DeleteTenantDialogProps) {
  const [open, setOpen] = useState(false);
  const [companyNameInput, setCompanyNameInput] = useState("");
  const [forceDelete, setForceDelete] = useState(false);
  const [forceConfirmation, setForceConfirmation] = useState("");
  const queryClient = useQueryClient();

  const deleteTenant = useMutation({
    mutationFn: deleteTenantMutation().mutationFn,
    onSuccess: () => {
      invalidateCellTenantQueries(queryClient, tenant.deployment_cell);
      setOpen(false);
      // Reset form
      setCompanyNameInput("");
      setForceDelete(false);
      setForceConfirmation("");
      toast.success("Tenant deleted successfully!");
    },
    onError: (error: unknown) => {
      console.error("Failed to delete tenant:", error);
      const errorDetail = getErrorMessage(error);
      toast.error("Failed to delete tenant", {
        description: errorDetail,
      });
    },
  });

  // Prevent copy-paste on confirmation inputs
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Prevent Ctrl+V / Cmd+V
    if (e.ctrlKey && e.key === "v") e.preventDefault();
    if (e.metaKey && e.key === "v") e.preventDefault();
  };

  // Check if all confirmations are valid
  const canDelete = () => {
    const companyNameMatch = companyNameInput.trim() === tenant.company.trim();
    const forceConfirmationValid =
      !forceDelete || forceConfirmation.toLowerCase() === "force";
    return companyNameMatch && forceConfirmationValid;
  };

  const handleDelete = () => {
    if (!canDelete()) return;

    deleteTenant.mutate({
      ...cellRequestOptions(tenant.deployment_cell),
      path: { tenant_id: tenant.id },
      query: { force: forceDelete },
    });
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      // Reset form when closing
      setCompanyNameInput("");
      setForceDelete(false);
      setForceConfirmation("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="destructive" size="sm">
            <Trash2 className="mr-1 h-3 w-3" />
            Delete
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="text-destructive h-5 w-5" />
            Delete Tenant - Security Confirmation
          </DialogTitle>
          <DialogDescription>
            This is a permanent action that cannot be undone. Complete all
            confirmations below.
          </DialogDescription>
        </DialogHeader>

        {/* Tenant Details */}
        <div className="py-4">
          <div className="bg-muted/50 rounded-lg border p-4">
            <div className="space-y-2">
              <h4 className="text-lg font-semibold">{tenant.company}</h4>
              <div className="text-muted-foreground space-y-1 text-sm">
                <div>
                  Subdomain:{" "}
                  <span className="font-mono">{tenant.subdomain}</span>
                </div>
                <div>
                  Tenant ID: <span className="font-mono">{tenant.id}</span>
                </div>
                {tenant.industry && (
                  <div className="mt-2">
                    <Badge variant="outline" className="text-xs">
                      {tenant.industry}
                    </Badge>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Confirmation Steps */}
        <div className="space-y-4">
          {/* Step 1: Company Name Confirmation */}
          <div className="space-y-2">
            <Label htmlFor="companyName" className="text-sm font-medium">
              Step 1: Type the exact company name to confirm
            </Label>
            <Input
              id="companyName"
              value={companyNameInput}
              onChange={(e) => {
                setCompanyNameInput(e.target.value);
              }}
              onPaste={handlePaste}
              onKeyDown={handleKeyDown}
              placeholder={`Type "${tenant.company}" exactly`}
              className="font-mono"
            />
            {companyNameInput &&
              companyNameInput.trim() !== tenant.company.trim() && (
                <p className="text-destructive text-xs">
                  Company name does not match exactly
                </p>
              )}
          </div>

          {/* Step 2: Force Delete Option */}
          <div className="space-y-3 border-t pt-4">
            <div className="flex items-start space-x-3">
              <Checkbox
                id="forceDelete"
                checked={forceDelete}
                onCheckedChange={(checked) => {
                  setForceDelete(checked as boolean);
                  if (!checked) setForceConfirmation("");
                }}
              />
              <div className="space-y-1">
                <Label
                  htmlFor="forceDelete"
                  className="flex items-center gap-2 text-sm font-medium"
                >
                  <Shield className="h-4 w-4" />
                  Force Delete (ignores Descope tenant errors)
                </Label>
                <p className="text-muted-foreground text-xs">
                  Check this if you want to delete the tenant even if there are
                  errors with the Descope tenant. This bypasses tenant
                  validation checks.
                </p>
              </div>
            </div>

            {/* Force Confirmation Input */}
            {forceDelete && (
              <div className="ml-6 space-y-2">
                <Label
                  htmlFor="forceConfirmation"
                  className="text-sm font-medium"
                >
                  Type "force" to confirm force deletion
                </Label>
                <Input
                  id="forceConfirmation"
                  value={forceConfirmation}
                  onChange={(e) => {
                    setForceConfirmation(e.target.value);
                  }}
                  onPaste={handlePaste}
                  onKeyDown={handleKeyDown}
                  placeholder="Type 'force' exactly"
                  className="max-w-32 font-mono"
                />
                {forceConfirmation &&
                  forceConfirmation.toLowerCase() !== "force" && (
                    <p className="text-destructive text-xs">
                      Must type "force" exactly
                    </p>
                  )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              handleOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteTenant.isPending || !canDelete()}
            className="text-white"
          >
            {deleteTenant.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              "Delete Tenant"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
